import type { InputParser } from "../types";

const intlFormat = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0
});

interface NumberOptions {
  allowNegative?: boolean;
  asString?: boolean;
  max?: number;
  maxDigits?: number;
}

function numberParser(opts?: {
  allowNegative?: boolean;
  max?: number;
  maxDigits?: number;
}): InputParser<number | null>;
function numberParser(opts: {
  allowNegative?: boolean;
  asString: true;
  max?: number;
  maxDigits?: number;
}): InputParser<string>;
function numberParser(
  opts?: NumberOptions
): InputParser<number | null> | InputParser<string> {
  const allowNegative = opts?.allowNegative ?? false;
  const asString = opts?.asString ?? false;
  const max = opts?.max;
  const maxDigits = opts?.maxDigits;
  const pattern = allowNegative ? /^-?\d*$/ : /^\d*$/;

  const validate = (raw: string) => {
    if (raw === "" || raw === "-") return true;
    if (!pattern.test(raw)) return false;
    if (maxDigits !== undefined) {
      const digits = raw.replace("-", "");
      if (digits.length > maxDigits) return false;
    }
    return true;
  };

  const clamp = (num: number) => {
    if (max !== undefined && num > max) return max;
    return num;
  };

  if (asString) {
    return {
      inputMode: "numeric",
      validate,
      parse: (raw) => {
        if (raw === "" || raw === "-") return "";
        const num = clamp(parseInt(raw, 10));
        return isNaN(num) ? "" : String(num);
      },
      format: (value: string) => {
        if (value === "") return "";
        const num = parseInt(value, 10);
        if (isNaN(num)) return "";
        return intlFormat.format(num);
      },
      toEditable: (value: string) => value
    } as InputParser<string>;
  }

  return {
    inputMode: "numeric",
    validate,
    parse: (raw) => {
      if (raw === "" || raw === "-") return null;
      const num = parseInt(raw, 10);
      if (isNaN(num)) return null;
      return clamp(num);
    },
    format: (value: number | null) => {
      if (value === null) return "";
      return intlFormat.format(value);
    },
    toEditable: (value: number | null) => {
      if (value === null) return "";
      return String(value);
    }
  };
}

export { numberParser };
